/**
 * Dashboard Page - Main overview dashboard
 *
 * Shows:
 * - Key performance metrics (Cash Collected, Revenue, Close Rate)
 * - Call statistics (Show Rate, Calls Due/Taken/Closed)
 * - Average metrics (Cash per Call, Close, Application, Opt-in)
 * - Additional stats (New Cash, MRR, Applications, Opt-ins)
 * - Charts (Closes by Country, Lead Source, Content Performance)
 * - Recent calls table
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LeadTimelineDrawer } from "../components/LeadTimelineDrawer";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton, FilterBar } from "../components";
import { AdditionalStatCard, AdditionalStatCardSkeleton } from "../components/StatCard";
import { RevenueCalendar } from "../components/RevenueCalendar";
import { branding } from "../config/branding";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { useTheme } from "../context/ThemeContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ResponsivePie } from "@nivo/pie";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";
import * as XLSX from "xlsx";

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TargetIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Chart colors from branding config
const CHART_COLORS = branding.colors.chart;

// Extended colors for pie charts
const PIE_COLORS = [
  "#3B82F6", // blue
  "#EF4444", // red
  "#10B981", // green
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#F97316", // orange
  "#84CC16", // lime
  "#6366F1", // indigo
  "#14B8A6", // teal
];

// Red shades palette for crimson theme
const CRIMSON_CHART_COLORS = [
  "#ef4444", // red-500
  "#dc2626", // red-600
  "#b91c1c", // red-700
  "#991b1b", // red-800
  "#7f1d1d", // red-900
  "#f87171", // red-400
  "#fca5a5", // red-300
  "#c53030", // deep red
  "#e53e3e", // mid red
  "#fc8181", // red-300 lighter
  "#be2626", // alt red-700
];

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatCurrencyAbbreviated = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return formatCurrency(value);
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatEasternDateTime = (value: string) => {
  if (!value || value === "Not recorded") return "Not recorded";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
};

const formatYAxis = (value: number) => {
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
};

interface DashboardMetrics {
  totalCashCollected: number;
  netCashAfterRefunds: number;
  refunds: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  schedulerCallsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  totalApplicationsCash: number;
  totalFullApplicationsCash: number;
  totalPartialApplicationsCash: number;
  totalOutboundCash: number;
  totalInboundCash: number;
  totalOptinsCash: number;
  newCashCollected: number;
  mrrCash: number;
  totalGrossRevenue: number;
  totalApplications: number;
  totalOptins: number;
  totalDMBookings: number;
  totalDMBookingsCash: number;
  cancelledCalls: number;
  callsBookedForCancelRate?: number;
  cancelRate: number;
  financialDQCount: number;
}

interface DashboardExportSection {
  title: string;
  metrics: DashboardMetrics;
}

interface DashboardLeadEvent {
  name: string;
  email: string;
  eventType: "Application" | "Opt-in" | "Booking" | "EOC";
  occurredAt: string;
  status: string;
  funnel: string;
}

type DashboardExportRow = { group: string; metric: string; value: string };

function dashboardExportRows(metrics: DashboardMetrics): DashboardExportRow[] {
  const count = (value: number) => Number(value || 0).toLocaleString("en-US");
  const money = (value: number) => formatCurrency(Number(value || 0));
  const percent = (value: number) => formatPercent(Number(value || 0));
  return [
    ["Revenue & cash", "Cash Collected", money(metrics.totalCashCollected)], ["Revenue & cash", "Net Cash After Refunds", money(metrics.netCashAfterRefunds)], ["Revenue & cash", "Refunds", money(metrics.refunds)], ["Revenue & cash", "New Cash Collected", money(metrics.newCashCollected)], ["Revenue & cash", "MRR", money(metrics.mrrCash)], ["Revenue & cash", "Revenue", money(metrics.totalRevenue)], ["Revenue & cash", "Total Gross Revenue", money(metrics.totalGrossRevenue)],
    ["Sales conversion", "Show Rate", percent(metrics.showRate)], ["Sales conversion", "Close Rate", percent(metrics.closeRate)], ["Sales conversion", "Cancel Rate", percent(metrics.cancelRate)], ["Sales conversion", "Calls Due", count(metrics.callsDue)], ["Sales conversion", "Calls Taken", count(metrics.callsTaken)], ["Sales conversion", "Calls Closed", count(metrics.callsClosed)], ["Sales conversion", "Cancelled Calls", count(metrics.cancelledCalls)], ["Sales conversion", "Financial DQs", count(metrics.financialDQCount)],
    ["Sales efficiency", "Avg. Cash / Call", money(metrics.avgCashPerCall)], ["Sales efficiency", "Avg. Cash / Close", money(metrics.avgCashPerClose)], ["Sales efficiency", "Avg. Cash / Application", money(metrics.avgCashPerApplication)], ["Sales efficiency", "Avg. Cash / Opt-in", money(metrics.avgCashPerOptin)],
    ["Lead & funnel", "Total Applications", count(metrics.totalApplications)], ["Lead & funnel", "Total Opt-ins", count(metrics.totalOptins)], ["Lead & funnel", "Total DM Bookings", count(metrics.totalDMBookings)], ["Lead & funnel", "Total DM Bookings Cash", money(metrics.totalDMBookingsCash)], ["Lead & funnel", "Cash from Applications", money(metrics.totalApplicationsCash)], ["Lead & funnel", "Cash from Full Applications", money(metrics.totalFullApplicationsCash)], ["Lead & funnel", "Cash from Partial Applications", money(metrics.totalPartialApplicationsCash)], ["Lead & funnel", "Cash from Opt-ins", money(metrics.totalOptinsCash)], ["Lead & funnel", "Cash from Outbound", money(metrics.totalOutboundCash)], ["Lead & funnel", "Cash from Inbound Booked Calls", money(metrics.totalInboundCash)],
  ].map(([group, metric, value]) => ({ group, metric, value }));
}

function downloadDashboardBlob(blob: Blob, filename: string) {
  const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = filename; link.click(); URL.revokeObjectURL(link.href);
}

function pdfText(value: string) { return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)").replace(/[–—]/g, "-"); }

export function buildDashboardExportPdf(dateLabel: string, sections: DashboardExportSection[], leadEvents: DashboardLeadEvent[] = []) {
  const pageWidth = 792; const pageHeight = 612; const left = 48; const right = 48; const rowHeight = 18; const headerY = 518; const footerY = 42;
  const pages: string[] = []; const text = (x: number, y: number, value: string, size = 9, bold = false, color = "0.12 0.12 0.14") => `BT /F${bold ? "2" : "1"} ${size} Tf ${color} rg 1 0 0 1 ${x} ${y} Tm (${pdfText(value)}) Tj ET`;
  // First page makes the three cohorts explicit, so a Webinar-only number can never be mistaken for the blended offer.
  const overviewRows = sections.map((section) => ({
    scope: section.title,
    cash: formatCurrency(section.metrics.totalCashCollected),
    revenue: formatCurrency(section.metrics.totalRevenue),
    applications: Number(section.metrics.totalApplications || 0).toLocaleString("en-US"),
    optins: Number(section.metrics.totalOptins || 0).toLocaleString("en-US"),
    closed: Number(section.metrics.callsClosed || 0).toLocaleString("en-US"),
  }));
  const overview = [text(left, 570, "Cr8Her Dashboard Export", 16, true), text(left, 551, dateLabel, 9, false, "0.32 0.35 0.4"), text(left, 526, "Cohort comparison", 12, true, "0.23 0.31 0.44"), text(left, 510, "Each funnel is a separate subset of the Blended Offer - not a replacement for the offer total.", 8, false, "0.32 0.35 0.4"), "0.23 0.31 0.44 rg", `${left} 476 ${pageWidth - left - right} 22 re f`];
  const summaryHeaders = [[56, "Cohort"], [260, "Cash"], [360, "Revenue"], [470, "Applications"], [575, "Opt-ins"], [670, "Closed"]] as const;
  summaryHeaders.forEach(([x, label]) => overview.push(text(x, 483, label, 8, true, "1 1 1")));
  overviewRows.forEach((row, index) => { const y = 454 - index * 30; overview.push(text(56, y, row.scope, 9, true), text(260, y, row.cash, 9), text(360, y, row.revenue, 9), text(470, y, row.applications, 9), text(575, y, row.optins, 9), text(670, y, row.closed, 9), `0.82 G 0.4 w ${left} ${y - 11} m ${pageWidth - right} ${y - 11} l S`); });
  overview.push(text(left, 330, "Detailed sections follow", 10, true, "0.23 0.31 0.44"), text(left, 312, "1. Blended Offer Metrics  ·  2. Webinar Funnel Metrics (Blended)  ·  3. Book a Call Funnel Metrics", 8, false), text(left, footerY, "Source: Dashboard summary · Eastern business date range", 7, false, "0.35 0.37 0.4"));
  pages.push(overview.join("\n"));
  for (const section of sections) {
    const rows = dashboardExportRows(section.metrics); let offset = 0;
    while (offset < rows.length) {
      const pageRows = rows.slice(offset, offset + 22); const continued = offset > 0; const commands = [text(left, 570, "Cr8Her Dashboard Export", 16, true), text(left, 551, dateLabel, 9, false, "0.32 0.35 0.4"), text(left, 530, `${section.title}${continued ? " (continued)" : ""}`, 11, true, "0.23 0.31 0.44"), "0.23 0.31 0.44 rg", `${left} ${headerY} ${pageWidth - left - right} 22 re f`, text(left + 8, headerY + 7, "Metric", 8, true, "1 1 1"), text(540, headerY + 7, "Value", 8, true, "1 1 1")];
      pageRows.forEach((row, index) => { const y = headerY - 22 - index * rowHeight; if (index === 0 || row.group !== pageRows[index - 1].group) commands.push(text(left + 8, y - 4, row.group.toUpperCase(), 7, true, "0.35 0.37 0.42")); commands.push(text(left + 150, y - 4, row.metric, 8, false), text(540, y - 4, row.value, 8, true)); commands.push(`0.82 G 0.35 w ${left} ${y - rowHeight} m ${pageWidth - right} ${y - rowHeight} l S`); });
      commands.push(text(left, footerY, "Source: Dashboard summary · Eastern business date range", 7, false, "0.35 0.37 0.4")); pages.push(commands.join("\n")); offset += pageRows.length;
    }
  }
  // One source-event row per record keeps every lead address readable and preserves the
  // application/opt-in/booking/EOC lineage an analyst needs to trace a contact's journey.
  const pdfCell = (value: string, max = 52) => value.length > max ? `${value.slice(0, max - 1)}…` : value;
  for (let offset = 0; offset < leadEvents.length; offset += 29) {
    const eventRows = leadEvents.slice(offset, offset + 29); const continued = offset > 0;
    const commands = [text(left, 570, "Cr8Her Dashboard Export", 16, true), text(left, 551, dateLabel, 9, false, "0.32 0.35 0.4"), text(left, 530, `Lead Activity Appendix${continued ? " (continued)" : ""}`, 11, true, "0.23 0.31 0.44"), text(left, 516, "One row per source record. Repeated contacts show their journey across applications, opt-ins, bookings, and EOCs.", 7, false, "0.32 0.35 0.4"), "0.23 0.31 0.44 rg", `${left} 494 ${pageWidth - left - right} 18 re f`, text(52, 500, "Lead name", 6.5, true, "1 1 1"), text(170, 500, "Email", 6.5, true, "1 1 1"), text(327, 500, "Event", 6.5, true, "1 1 1"), text(386, 500, "Occurred (ET)", 6.5, true, "1 1 1"), text(475, 500, "Status / outcome", 6.5, true, "1 1 1"), text(610, 500, "Funnel / medium", 6.5, true, "1 1 1")];
    eventRows.forEach((row, index) => { const y = 481 - index * 15; commands.push(text(52, y, row.name || "Not recorded", 6.3), text(170, y, row.email || "Not recorded", 6.3), text(327, y, row.eventType, 6.3, true), text(386, y, row.occurredAt, 6.3), text(475, y, pdfCell(row.status, 25), 6.3), text(610, y, pdfCell(row.funnel, 21), 6.3), `0.86 G 0.25 w ${left} ${y - 7} m ${pageWidth - right} ${y - 7} l S`); });
    commands.push(text(left, footerY, `Lead Activity Appendix · ${leadEvents.length.toLocaleString("en-US")} source records · Eastern business date range`, 7, false, "0.35 0.37 0.4")); pages.push(commands.join("\n"));
  }
  const objects: string[] = ["<< /Type /Catalog /Pages 2 0 R >>", ""]; const pageNumbers = pages.map((_, index) => 3 + index * 2); objects[1] = `<< /Type /Pages /Kids [${pageNumbers.map((n) => `${n} 0 R`).join(" ")}] /Count ${pages.length} >>`;
  pages.forEach((content, index) => { const page = 3 + index * 2; objects[page - 1] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${pages.length * 2 + 3} 0 R /F2 ${pages.length * 2 + 4} 0 R >> >> /Contents ${page + 1} 0 R >>`; objects[page] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`; });
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>", "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>"); let pdf = "%PDF-1.4\n"; const offsets = [0]; objects.forEach((object, index) => { offsets.push(pdf.length); pdf += `${index + 1} 0 obj\n${object}\nendobj\n`; }); const xref = pdf.length; pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`; offsets.slice(1).forEach((value) => { pdf += `${String(value).padStart(10, "0")} 00000 n \n`; }); pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`; return new Blob([pdf], { type: "application/pdf" });
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage: number;
  revenue?: number;
}

interface TrendPoint {
  date: string;
  value: number;
}

interface CashByCreatorItem {
  name: string;
  cash: number;
  percentage: number;
}

interface RecentCall {
  id: string;
  _id: string;
  timestamp: string;
  dateCallWasTaken: string;
  leadName: string;
  email: string;
  phone: string;
  closer: string;
  setter: string;
  status: "closed" | "no-show" | "pending" | "not-interested";
  callOutcome: string;
  cashCollected: number;
  notes: string;
  leadSourcePlatform?: string;
  leadSourceContent?: string;
}

// Field definitions for editing calls
const CALL_EDIT_FIELDS: FieldDefinition[] = [
  { key: "leadName", label: "Lead Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "closer", label: "Closer", type: "text" },
  { key: "setter", label: "Setter", type: "text" },
  { key: "callOutcome", label: "Call Outcome", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

interface FilterOption {
  value: string;
  label: string;
}

interface ContentOption extends FilterOption {
  allValues?: string[]; // Multiple video IDs that map to the same title
}

interface FilterOptions {
  closers: string[];
  setters: string[];
  funnels: FilterOption[]; // Now has value/label for normalized display
  contents: ContentOption[];
  countries: string[];
  sources: string[];
  creators: string[];
}

// Nivo pie chart common theme
const getNivoPieTheme = () => ({
  labels: {
    text: {
      fontSize: 12,
      fontWeight: 400,
    },
  },
});

// Custom tooltip component for Nivo pie charts (works in dark/light mode)
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: datum.color }}
      />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

// Convert chart data to Nivo format
// First deduplicate by name (merge items with same name), then assign colors
const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  // Merge items with the same name
  const mergedMap = new Map<string, { value: number; revenue: number }>();
  for (const item of data) {
    const existing = mergedMap.get(item.name);
    if (existing) {
      existing.value += item.value;
      existing.revenue += item.revenue || 0;
    } else {
      mergedMap.set(item.name, { value: item.value, revenue: item.revenue || 0 });
    }
  }

  // Convert back to array and calculate percentages
  const merged = Array.from(mergedMap.entries()).map(([name, data]) => ({
    name,
    value: data.value,
    revenue: data.revenue,
  }));

  // Sort by value descending, then by name alphabetically for stable ordering
  // This prevents items with the same value from jumping around on re-renders
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.name.localeCompare(b.name);
  });

  // Calculate total for percentages
  const total = merged.reduce((sum, item) => sum + item.value, 0);

  return merged.map((item, index) => ({
    id: item.name, // Name is now unique after merging
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
};

// ─── KPI Bar ─────────────────────────────────────────────────────────────────
type KpiPeriod = "month" | "90days" | "year";

const KPI_PERIOD_LABELS: Record<KpiPeriod, string> = {
  month: "This Month",
  "90days": "90 Days",
  year: "This Year",
};

function KPIBar({ isAdmin, currentCash }: { isAdmin: boolean; currentCash: number }) {
  const { theme: kpiTheme } = useTheme();
  const kpiDark = kpiTheme !== "light" && kpiTheme !== "crimson";
  const [period, setPeriod] = useState<KpiPeriod>("month");
  const [target, setTarget] = useState(0);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchKpi = useCallback(async (p: KpiPeriod) => {
    try {
      const res = await fetch(`/api/kpi?period=${p}`);
      const json = await res.json() as { success: boolean; data?: { target: number } };
      if (json.success && json.data) {
        setTarget(json.data.target);
        
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchKpi(period); }, [period, fetchKpi]);

  const saveTarget = async () => {
    const val = parseFloat(editValue.replace(/[^0-9.]/g, ""));
    if (isNaN(val) || val < 0) return;
    setSaving(true);
    try {
      await fetch("/api/kpi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period, target: val }),
      });
      setTarget(val);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const pct = target > 0 ? Math.min((currentCash / target) * 100, 100) : 0;
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(2)}M`
      : n >= 1_000
      ? `$${(n / 1_000).toFixed(1)}K`
      : `$${Math.round(n)}`;

  const barGlowColor = pct < 33 ? "#ef4444" : pct < 60 ? "#f97316" : "#22c55e";
  const pctTextColor = pct < 33 ? "#ef4444" : pct < 60 ? "#f97316" : "#22c55e";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.4 }}
      className="relative w-full rounded-xl border border-border overflow-hidden bg-card"
    >
      {/* Ambient glow strip at top */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--brand-gradient-start), var(--brand-gradient-end), transparent)" }}
      />

      <div className="flex items-center gap-4 px-4 py-3">
        {/* Left: Label + Period */}
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <span className={`text-[10px] uppercase tracking-widest font-semibold ${kpiDark ? "text-white/30" : "text-foreground/40"}`}>Revenue Goal</span>
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value as KpiPeriod); setEditing(false); }}
            className={`text-sm font-bold bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-4 ${kpiDark ? "text-white" : "text-foreground"}`}
            style={{ backgroundImage: kpiDark ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpath d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")` : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(0,0,0,0.4)' stroke-width='2'%3E%3Cpath d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
          >
            {(Object.keys(KPI_PERIOD_LABELS) as KpiPeriod[]).map((p) => (
              <option key={p} value={p} style={{ background: "#1a1a2e" }}>{KPI_PERIOD_LABELS[p]}</option>
            ))}
          </select>
        </div>

        {/* Divider */}
        <div className={`w-px h-8 flex-shrink-0 ${kpiDark ? "bg-white/10" : "bg-foreground/10"}`} />

        {/* Center: Progress bar + amounts */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {/* Bar track — clickable to open revenue calendar */}
          <div
            role="button"
            tabIndex={0}
            title="View revenue calendar"
            onClick={() => setCalendarOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setCalendarOpen(true); }}
            className={`w-full h-2 rounded-full overflow-hidden cursor-pointer transition-colors relative group ${kpiDark ? "bg-white/10 hover:bg-white/15" : "bg-foreground/10 hover:bg-foreground/15"}`}
          >
            <motion.div
              className="h-full rounded-full relative"
              style={{
                background: "linear-gradient(90deg, #ef4444 0%, #f97316 45%, #eab308 70%, #22c55e 100%)",
                boxShadow: pct > 5 ? `0 0 8px 1px ${barGlowColor}` : "none",
              }}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
            />
          </div>

          {/* Amount labels */}
          <div className="flex items-center justify-between">
            <span className={`text-xs tabular-nums ${kpiDark ? "text-white/50" : "text-foreground/50"}`}>
              <span className={`font-semibold ${kpiDark ? "text-white" : "text-foreground"}`}>{fmt(currentCash)}</span>
              {target > 0 && <span className={kpiDark ? "text-white/40" : "text-foreground/40"}> / {fmt(target)}</span>}
            </span>
            {target > 0 && (
              <span
                className="text-xs font-bold tabular-nums"
                style={{ color: pctTextColor }}
              >
                {pct.toFixed(1)}%
              </span>
            )}
            {target === 0 && (
              <span className={`text-xs italic ${kpiDark ? "text-white/25" : "text-foreground/30"}`}>No target set</span>
            )}
          </div>
        </div>

        {/* Right: Admin edit */}
        {isAdmin && (
          <div className="flex-shrink-0">
            {editing ? (
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1 rounded-lg px-2 py-1 border ${kpiDark ? "bg-white/5 border-white/15" : "bg-foreground/5 border-foreground/15"}`}>
                  <span className={`text-xs font-medium ${kpiDark ? "text-white/40" : "text-foreground/40"}`}>$</span>
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveTarget(); if (e.key === "Escape") setEditing(false); }}
                    placeholder="500000"
                    autoFocus
                    className={`w-24 text-sm bg-transparent focus:outline-none font-medium ${kpiDark ? "text-white placeholder-white/20" : "text-foreground placeholder-foreground/20"}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={saveTarget}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, var(--brand-gradient-start), var(--brand-gradient-end))" }}
                >
                  {saving ? "…" : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(false)} className={`transition-colors text-xs px-1 ${kpiDark ? "text-white/30 hover:text-white/60" : "text-foreground/30 hover:text-foreground/60"}`}>✕</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setEditValue(target > 0 ? String(target) : ""); setEditing(true); }}
                title="Set KPI target"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${kpiDark ? "text-white/50 hover:text-white/90 bg-white/5 hover:bg-white/10 border-white/10" : "text-foreground/50 hover:text-foreground/90 bg-foreground/5 hover:bg-foreground/10 border-foreground/10"}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Set Target
              </button>
            )}
          </div>
        )}
      </div>

      <RevenueCalendar open={calendarOpen} onClose={() => setCalendarOpen(false)} />
    </motion.div>
  );
}

export function Dashboard() {
  const { theme } = useTheme();
  const isCrimson = theme === "crimson";
  const chartColors = CRIMSON_CHART_COLORS;
  const areaStroke = "#991b1b";

  // Get current user session
  const { data: session } = useSession();

  // Use global date range from context (synced with header date picker)
  const { dateRange: globalDateRange, setDateRange, formatForApi, formatForDisplay } = useDateRange();

  // Use shared filters from context (synced with URL via nuqs)
  const {
    dateField,
    closer,
    closers,
    setter,
    setters,
    funnel,
    funnels,
    leadType,
    leadTypes,
    content,
    contents,
    country,
    source,
    sources,
    creators,
    setDateField,
    setClosers,
    setSetters,
    setFunnels,
    setLeadTypes,
    setContents,
    setCountry,
    setSource,
    setCountries,
    setSources,
    setCreators,
    clearAllFilters,
  } = useFilters();

  // Data states
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendPoint[]>([]);
  const [trendType, setTrendType] = useState<"daily" | "monthly">("monthly");
  const [countryData, setCountryData] = useState<ChartDataItem[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<ChartDataItem[]>([]);
  const [contentData, setContentData] = useState<ChartDataItem[]>([]);
  const [cashByCreator, setCashByCreator] = useState<CashByCreatorItem[]>([]);
  const [isCashByCreatorOpen, setIsCashByCreatorOpen] = useState(false);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  // Server-side pagination state
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    creators: [],
    sources: [],
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [exportingDashboard, setExportingDashboard] = useState<"csv" | "xlsx" | "pdf" | null>(null);

  // Drilldown modal state for Financial DQs, Cancelled Calls, and Refunds
  interface DrilldownLead {
    name: string; email: string; date?: string; callOutcome?: string; status?: string; closer?: string;
    amount?: number; product?: string; processor?: string; attribution?: string;
    sourceCreator?: string; sourcePlatform?: string; sourceFunnel?: string; sourceContent?: string;
    cancelledAt?: string; scheduledCallAt?: string; cancellationReason?: string; reasonSource?: string;
  }
  interface DrilldownFilterOptions { creators: string[]; platforms: string[]; funnels: string[]; contents: string[]; }
  type DrilldownType = "financialDQ" | "cancelledCalls" | "refunds";
  const [drilldownModal, setDrilldownModal] = useState<{ type: DrilldownType; leads: DrilldownLead[]; filterOptions: DrilldownFilterOptions } | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownFilters, setDrilldownFilters] = useState<{ sourceCreator: string; sourcePlatform: string; sourceFunnel: string; sourceContent: string }>({ sourceCreator: "", sourcePlatform: "", sourceFunnel: "", sourceContent: "" });
  const formatEasternDateTime = (value?: string) => value ? new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }).format(new Date(value)) : "Not available";

  // Call Bookings funnel cohort: inbound is self-booked; outbound is setter-booked.
  const [bookingChannel, setBookingChannel] = useState<"" | "inbound" | "outbound">("");
  // High-level sales cohort, intentionally independent from the detailed Source Funnel filter.
  const [funnelType, setFunnelType] = useState<"" | "bookCall" | "webinar">("");

  // Chart filter mode: "closed" shows only closed leads, "all" shows all leads
  const [chartFilterMode, setChartFilterMode] = useState<"closed" | "all">("closed");

  // Edit/Delete state for recent calls
  const [editingCall, setEditingCall] = useState<RecentCall | null>(null);
  const [deletingCall, setDeletingCall] = useState<RecentCall | null>(null);

  // Lead timeline drawer
  const [selectedLeadEmail, setSelectedLeadEmail] = useState<string | null>(null);

  // Status badge helper
  const getStatusBadgeClass = (status: RecentCall["status"]) => {
    const styles: Record<string, string> = {
      closed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      "no-show": "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
      "not-interested": "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    };
    return styles[status] || styles.pending;
  };

  const getStatusLabel = (status: RecentCall["status"]) => {
    const labels: Record<string, string> = {
      closed: "Closed",
      "no-show": "No Show",
      pending: "Pending",
      "not-interested": "Not Interested",
    };
    return labels[status] || status;
  };

  // Define columns for recent calls table
  const recentCallsColumns: ColumnDef<RecentCall>[] = useMemo(() => [
    {
      id: "timestamp",
      header: "Timestamp",
      cell: (row) => {
        if (!row.timestamp) return <span className="text-muted-foreground">-</span>;
        const date = new Date(row.timestamp);
        return (
          <span className="text-muted-foreground text-xs">
            {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
    },
    {
      id: "dateCallWasTaken",
      header: "Date Call Was Taken",
      cell: (row) => (
        row.dateCallWasTaken ? (
          <span className="text-muted-foreground">{row.dateCallWasTaken}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
    },
    {
      id: "leadName",
      header: "Lead Name",
      accessorKey: "leadName",
      className: "font-medium",
    },
    {
      id: "closer",
      header: "Closer",
      accessorKey: "closer",
    },
    {
      id: "setter",
      header: "Setter",
      accessorKey: "setter",
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => {
        const platform = row.leadSourcePlatform || "";
        const content = row.leadSourceContent || "";
        if (!platform && !content) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-xs text-muted-foreground">
            {platform}{platform && content ? " | " : ""}{content}
          </span>
        );
      },
    },
    {
      id: "status",
      header: "Call Outcome",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(row.status)}`}>
          {row.callOutcome || "-"}
        </span>
      ),
    },
    {
      id: "cashCollected",
      header: "Cash Collected",
      cell: (row) => (
        row.cashCollected > 0 ? (
          <span className="text-green-600 font-medium">{formatCurrency(row.cashCollected)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
      headerClassName: "text-right",
      className: "text-right",
    },
  ], []);

  // Handle edit
  const handleEditCall = (call: RecentCall) => {
    setEditingCall(call);
  };

  // Handle save
  const handleSaveCall = async (data: Record<string, unknown>) => {
    // Map frontend field names to backend field names
    const mappedData = {
      prospectName: data.leadName,
      prospectEmail: data.email,
      prospectNumber: data.phone,
      closerName: data.closer,
      setterName: data.setter,
      callOutcome: data.callOutcome,
      callNotes: data.notes,
    };

    const response = await fetch(`/api/leads/${data._id || data.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      throw new Error("Failed to update call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle delete
  const handleDeleteCall = (call: RecentCall) => {
    setDeletingCall(call);
  };

  // Confirm delete
  const confirmDeleteCall = async () => {
    if (!deletingCall) return;

    const response = await fetch(`/api/leads/${deletingCall._id || deletingCall.id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete call");
    }

    // Refresh data
    fetchData(false);
  };

  // Fetch data from API
  // tableOnly: if true, only show table loading (for pagination changes)
  const fetchData = useCallback(async (showLoading = true, tableOnly = false) => {
    if (showLoading) {
      if (tableOnly) {
        setTableLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      // Use global date range from context
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadTypes.length > 0) params.set("leadType", leadTypes.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      if (country) params.set("country", country);
      if (sources.length > 0) params.set("source", sources.join(","));
      if (creators.length > 0) params.set("creator", creators.join(","));
      if (bookingChannel) params.set("bookingChannel", bookingChannel);
      if (funnelType) params.set("funnelType", funnelType);
      // Chart filter: closedOnly=true shows only closed leads in charts
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");
      // Pagination params
      params.set("page", String(tablePage));
      params.set("pageSize", String(tablePageSize));

      const response = await fetch(`/api/dashboard/summary?${params}`);
      const result = await response.json();

      if (!result.success) throw new Error(result.error);

      const { data } = result;

      setMetrics({
        totalCashCollected: data.metrics.totalCashCollected || 0,
        netCashAfterRefunds: data.metrics.netCashAfterRefunds || 0,
        refunds: data.metrics.refunds || 0,
        totalRevenue: data.metrics.totalRevenue || 0,
        closeRate: data.metrics.closeRate || 0,
        showRate: data.metrics.showRate || 0,
        callsDue: data.metrics.callsDue || 0,
        schedulerCallsDue: data.metrics.schedulerCallsDue || 0,
        callsTaken: data.metrics.callsTaken || 0,
        callsClosed: data.metrics.callsClosed || 0,
        avgCashPerCall: data.metrics.avgCashPerCall || 0,
        avgCashPerClose: data.metrics.avgCashPerClose || 0,
        avgCashPerApplication: data.metrics.avgCashPerApplication || 0,
        avgCashPerOptin: data.metrics.avgCashPerOptin || 0,
        totalApplicationsCash: data.metrics.totalApplicationsCash || 0,
        totalFullApplicationsCash: data.metrics.totalFullApplicationsCash || 0,
        totalPartialApplicationsCash: data.metrics.totalPartialApplicationsCash || 0,
        totalOutboundCash: data.metrics.totalOutboundCash || 0,
        totalInboundCash: data.metrics.totalInboundCash || 0,
        totalOptinsCash: data.metrics.totalOptinsCash || 0,
        newCashCollected: data.metrics.newCashCollected || 0,
        mrrCash: data.metrics.mrrCash || 0,
        totalGrossRevenue: data.metrics.totalGrossRevenue || 0,
        totalApplications: data.metrics.totalApplications || 0,
        totalOptins: data.metrics.totalOptins || 0,
        totalDMBookings: data.metrics.totalDMBookings || 0,
        totalDMBookingsCash: data.metrics.totalDMBookingsCash || 0,
        cancelledCalls: data.metrics.cancelledCalls || 0,
        callsBookedForCancelRate: data.metrics.callsBookedForCancelRate || 0,
        cancelRate: data.metrics.cancelRate || 0,
        financialDQCount: data.metrics.financialDQCount || 0,
      });

      if (data.filterOptions) {
        setFilterOptions({
          closers: data.filterOptions.closers || [],
          setters: data.filterOptions.setters || [],
          funnels: data.filterOptions.funnels || [],
          contents: data.filterOptions.contents || [],
          countries: data.filterOptions.countries || [],
          sources: data.filterOptions.sources || [],
          creators: data.filterOptions.creators || [],
        });
      }

      setCashTrend(data.cashTrend || []);
      setCashByCreator(data.cashByCreator || []);
      setTrendType(data.trendType || "monthly");
      setCountryData(data.countryData || []);
      setLeadSourceData(data.leadSourceData || []);
      setContentData(data.contentData || []);
      setRecentCalls(data.recentCalls || []);

      // Update pagination state from server response
      if (data.pagination) {
        setTableTotalItems(data.pagination.totalItems);
        setTableTotalPages(data.pagination.totalPages);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    } finally {
      if (showLoading) {
        if (tableOnly) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [formatForApi, dateField, closers, setters, funnels, leadTypes, contents, country, sources, creators, bookingChannel, funnelType, chartFilterMode, tablePage, tablePageSize]);

  const downloadDashboardExport = async (format: "csv" | "xlsx" | "pdf") => {
    setExportingDashboard(format);
    try {
      const { startDate, endDate } = formatForApi();
      const buildParams = (scope?: "bookCall" | "webinar") => {
        const params = new URLSearchParams({ startDate, endDate, closedOnly: chartFilterMode === "closed" ? "true" : "false" });
        if (dateField) params.set("dateField", dateField);
        if (closers.length) params.set("closer", closers.join(",")); if (setters.length) params.set("setter", setters.join(",")); if (funnels.length) params.set("funnel", funnels.join(",")); if (leadTypes.length) params.set("leadType", leadTypes.join(",")); if (contents.length) params.set("content", contents.join(",")); if (country) params.set("country", country); if (sources.length) params.set("source", sources.join(",")); if (creators.length) params.set("creator", creators.join(","));
        // Deliberately omit the interactive booking-channel/funnel-type selector: this export always contains all three comparable cohorts.
        if (scope) params.set("funnelType", scope);
        return params;
      };
      const [results, leadResponse] = await Promise.all([
        Promise.all([undefined, "webinar" as const, "bookCall" as const].map(async (scope) => {
          const response = await fetch(`/api/dashboard/summary?${buildParams(scope)}`); const body = await response.json(); if (!response.ok || !body.success) throw new Error(body.error || "Unable to prepare dashboard export"); return body.data.metrics as DashboardMetrics;
        })),
        fetch(`/api/dashboard/export-leads?${buildParams()}`).then(async (response) => {
          const body = await response.json();
          if (!response.ok || !body.success) throw new Error(body.error || "Unable to prepare lead activity appendix");
          return body.data.events as DashboardLeadEvent[];
        }),
      ]);
      const sections: DashboardExportSection[] = [{ title: "Blended Offer Metrics", metrics: results[0] }, { title: "Webinar Funnel Metrics (Blended)", metrics: results[1] }, { title: "Book a Call Funnel Metrics", metrics: results[2] }];
      const leadEvents = leadResponse.map((event) => ({ ...event, occurredAt: formatEasternDateTime(event.occurredAt) }));
      const filename = `cr8her-dashboard-${startDate}-to-${endDate}`;
      if (format === "pdf") downloadDashboardBlob(buildDashboardExportPdf(`Selected dates: ${formatForDisplay()} · Eastern business dates`, sections, leadEvents), `${filename}.pdf`);
      else {
        const metricMatrix = [["Section", "Metric Group", "Metric", "Value"], ...sections.flatMap((section) => dashboardExportRows(section.metrics).map((row) => [section.title, row.group, row.metric, row.value]))];
        const leadMatrix = [["Lead Name", "Email", "Event Type", "Occurred At (ET)", "Status / Outcome", "Funnel / Medium"], ...leadEvents.map((event) => [event.name, event.email, event.eventType, event.occurredAt, event.status, event.funnel])];
        if (format === "csv") {
          const csv = [metricMatrix[0], ...metricMatrix.slice(1), [], ["Lead Activity Appendix — one row per source record; repeated contacts show applications, opt-ins, bookings, and EOCs"], ...leadMatrix].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
          downloadDashboardBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${filename}.csv`);
        } else {
          const metricsSheet = XLSX.utils.aoa_to_sheet(metricMatrix); metricsSheet["!cols"] = [{ wch: 34 }, { wch: 22 }, { wch: 36 }, { wch: 20 }];
          const leadsSheet = XLSX.utils.aoa_to_sheet(leadMatrix); leadsSheet["!cols"] = [{ wch: 28 }, { wch: 38 }, { wch: 14 }, { wch: 22 }, { wch: 30 }, { wch: 28 }];
          const workbook = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(workbook, metricsSheet, "Dashboard Metrics"); XLSX.utils.book_append_sheet(workbook, leadsSheet, "Lead Activity"); XLSX.writeFile(workbook, `${filename}.xlsx`);
        }
      }
    } catch (error) { console.error("Failed to export dashboard metrics:", error); window.alert(error instanceof Error ? error.message : "Unable to prepare dashboard export"); }
    finally { setExportingDashboard(null); }
  };

  // Auto-remove invalid filters when available options change
  // (e.g., when selecting a funnel that has no data for certain closers)
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: Record<string, string[]> = {};

    // Check closer filter - if selected closers not in available options, remove them
    if (closers.length > 0 && filterOptions.closers.length > 0) {
      if (!closers.every(c => filterOptions.closers.includes(c))) {
        filtersToRemove.closers = [];
      }
    }

    // Check setter filter
    if (setters.length > 0 && filterOptions.setters.length > 0) {
      if (!setters.every(s => filterOptions.setters.includes(s))) {
        filtersToRemove.setters = [];
      }
    }

    // Check funnel filter (uses value/label format)
    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnels.every(f => funnelValues.includes(f))) {
        filtersToRemove.funnels = [];
      }
    }

    // Check content filter (uses value/label format)
    if (contents.length > 0 && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contents.every(c => contentValues.includes(c))) {
        filtersToRemove.contents = [];
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0) {
      if (!filterOptions.countries.includes(country)) {
        filtersToRemove.countries = [];
      }
    }

    // Check source filter
    if (sources.length > 0 && filterOptions.sources.length > 0) {
      if (!sources.every(s => filterOptions.sources.includes(s))) {
        filtersToRemove.sources = [];
      }
    }

    // Check creator filter
    if (creators.length > 0 && filterOptions.creators.length > 0) {
      if (!creators.every(c => filterOptions.creators.includes(c))) {
        filtersToRemove.creators = [];
      }
    }

    // Remove invalid filters
    if (Object.keys(filtersToRemove).length > 0) {
      if ('closers' in filtersToRemove) setClosers([]);
      if ('setters' in filtersToRemove) setSetters([]);
      if ('funnels' in filtersToRemove) setFunnels([]);
      if ('contents' in filtersToRemove) setContents([]);
      if ('countries' in filtersToRemove) setCountries([]);
      if ('sources' in filtersToRemove) setSources([]);
      if ('creators' in filtersToRemove) setCreators([]);
    }
  }, [filterOptions, closers, setters, funnels, contents, country, sources, creators, setClosers, setSetters, setFunnels, setContents, setCountries, setSources, setCreators]);

  // Track if the next fetch should be table-only (for pagination changes)
  const isPaginationChangeRef = useRef(false);

  // Reset to page 1 when filters change
  useEffect(() => {
    isPaginationChangeRef.current = false;
    setTablePage(1);
  }, [globalDateRange, dateField, closers, setters, funnels, leadTypes, contents, country, sources, creators, bookingChannel, funnelType, chartFilterMode]);

  // Handle pagination changes - mark as pagination-only before state update
  const handlePageChange = (page: number) => {
    isPaginationChangeRef.current = true;
    setTablePage(page);
  };

  const handlePageSizeChange = (size: number) => {
    isPaginationChangeRef.current = true;
    setTablePageSize(size);
    setTablePage(1);
  };

  // Fetch data when dependencies change
  useEffect(() => {
    const isTableOnly = isPaginationChangeRef.current;
    isPaginationChangeRef.current = false; // Reset for next change
    fetchData(true, isTableOnly);
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchData(false), 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Clear all filters (date range is managed globally by the header picker)
  const handleClearAll = () => {
    clearAllFilters();
    setBookingChannel("");
    setFunnelType("");
  };

  // Chart click handlers
  const handleCountryClick = (name: string) => {
    if (name === "Other") return;
    setCountry(country === name ? "" : name);
  };

  const handleSourceClick = (name: string) => {
    if (name === "Other" || name === "N/A") return;
    setSource(source === name ? "" : name);
  };

  const handleContentClick = (name: string) => {
    if (name === "Other") return;
    const matchingOption = filterOptions.contents.find(
      (c) => c.value === name || c.label === name || (c.value && name.startsWith(c.label))
    );
    const valueToSet = matchingOption?.value || name;
    if (contents.includes(valueToSet)) {
      setContents(contents.filter(c => c !== valueToSet));
    } else {
      setContents([...contents, valueToSet]);
    }
  };

  const openDrilldown = async (type: DrilldownType, filters?: typeof drilldownFilters) => {
    setDrilldownLoading(true);
    if (!filters) setDrilldownFilters({ sourceCreator: "", sourcePlatform: "", sourceFunnel: "", sourceContent: "" });
    const f = filters || { sourceCreator: "", sourcePlatform: "", sourceFunnel: "", sourceContent: "" };
    const emptyFilterOptions: DrilldownFilterOptions = { creators: [], platforms: [], funnels: [], contents: [] };
    setDrilldownModal(prev => prev ? { ...prev, leads: [] } : { type, leads: [], filterOptions: emptyFilterOptions });
    try {
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("startDate", apiDates.startDate);
      params.set("endDate", apiDates.endDate);
      if (dateField) params.set("dateField", dateField);
      if (f.sourceCreator) params.set("sourceCreator", f.sourceCreator);
      if (f.sourcePlatform) params.set("sourcePlatform", f.sourcePlatform);
      if (f.sourceFunnel) params.set("sourceFunnel", f.sourceFunnel);
      if (f.sourceContent) params.set("sourceContent", f.sourceContent);
      // Cancelled calls follow the card's Funnel Type partition (booking status, Oct 19 2026).
      if (type === "cancelledCalls" && funnelType) params.set("funnelType", funnelType);
      // Refunds follow the Dashboard's active source filters as well as modal filters.
      if (type === "refunds") {
        if (creators.length) params.set("creator", creators.join(","));
        if (sources.length) params.set("source", sources.join(","));
        if (funnels.length) params.set("funnel", funnels.join(","));
        if (contents.length) params.set("content", contents.join(","));
      }
      const endpoint = type === "financialDQ"
        ? "/api/dashboard/financial-dqs"
        : type === "cancelledCalls"
          ? "/api/dashboard/cancelled-calls"
          : "/api/dashboard/refunds";
      const res = await fetch(`${endpoint}?${params}`);
      const data = await res.json();
      if (data.success) setDrilldownModal({ type, leads: type === "refunds" ? (data.refunds || []) : (data.leads || []), filterOptions: data.filterOptions || emptyFilterOptions });
    } catch (e) {
      console.error("Drilldown fetch failed:", e);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid grid-cols-4 gap-4">
          {[0, 1, 2].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Call Statistics Skeleton */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Average Metrics Skeleton */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i + 4} />
          ))}
        </div>

        {/* Cash Totals Skeleton */}
        <div className="grid gap-4 grid-cols-2">
          {[0, 1].map((i) => (
            <StatCardSkeleton key={i} index={i + 8} />
          ))}
        </div>

        {/* Additional Statistics Skeleton */}
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <AdditionalStatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
        >
          <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
          <div className="h-[400px] rounded-lg bg-muted/30 animate-pulse flex items-center justify-center">
            <motion.div
              className="w-32 h-32 rounded-full border-4 border-muted border-t-muted-foreground/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <motion.div
            className="absolute inset-0"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
            }}
          />
        </motion.div>
      </div>
    );
  }

  // Get greeting based on time of day with user's first name
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };


  return (
    <div className="space-y-4">
      {/* Row 1: Greeting + Controls */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between gap-2"
      >
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-lg md:text-2xl font-bold tracking-tight flex-shrink-0"
        >
          {getGreeting()}
        </motion.h1>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <div className="hidden lg:flex items-center gap-1 rounded-lg border border-border bg-card p-1" aria-label="Download dashboard metrics">
            <button onClick={() => downloadDashboardExport("csv")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard === "csv" ? "Preparing…" : "↓ CSV"}</button>
            <button onClick={() => downloadDashboardExport("xlsx")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard === "xlsx" ? "Preparing…" : "↓ Excel"}</button>
            <button onClick={() => downloadDashboardExport("pdf")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard === "pdf" ? "Preparing…" : "↓ PDF"}</button>
          </div>
          <div className="flex lg:hidden items-center gap-1 rounded-lg border border-border bg-card p-1" aria-label="Download dashboard metrics">
            <button onClick={() => downloadDashboardExport("pdf")} disabled={!!exportingDashboard} className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50">{exportingDashboard ? "Preparing…" : "↓ Export PDF"}</button>
          </div>
          <AnimatePresence mode="wait">
            {lastUpdated && (
              <motion.span
                key="updated"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="text-xs text-muted-foreground hidden sm:block"
              >
                Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </motion.span>
            )}
          </AnimatePresence>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${
              autoRefresh
                ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400"
                : "bg-muted text-muted-foreground"
            }`}
          >
            <motion.span
              animate={autoRefresh ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
              className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${autoRefresh ? "bg-green-500" : "bg-gray-400"}`}
            />
            <span className="hidden xs:inline">{autoRefresh ? "Live" : "Paused"}</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={() => fetchData(false)}
            className="p-1.5 md:p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
            title="Refresh now"
          >
            <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </motion.button>
        </div>
      </motion.div>

      {/* Row 2: KPI Revenue Goal Card */}
      <KPIBar isAdmin={(session?.user as { role?: string } | undefined)?.role === "admin"} currentCash={metrics?.totalCashCollected ?? 0} />

      {/* Row 3: Filters - all in one uniform line */}
      <FilterBar
        dateLabel={formatForDisplay()}
        showDateFieldToggle={true}
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={[
          {
            key: "bookingChannel",
            label: "Call Booking Type",
            options: [{ value: "inbound", label: "Inbound (Self-Booked)" }, { value: "outbound", label: "Outbound (Setter Booked)" }],
            value: bookingChannel,
            onChange: (value) => setBookingChannel(value as "" | "inbound" | "outbound"),
          },
          {
            key: "funnelType",
            label: "Funnel Type",
            options: [{ value: "bookCall", label: "Book a Call" }, { value: "webinar", label: "Webinar" }],
            value: funnelType,
            onChange: (value) => setFunnelType(value as "" | "bookCall" | "webinar"),
          },
          {
            key: "creator",
            label: "Source Creator",
            options: filterOptions.creators.map((c) => ({ value: c, label: c })),
            value: creators,
            onChange: (v) => setCreators(v as string[]),
            multiple: true,
          },
          {
            key: "source",
            label: "Source Platform",
            options: filterOptions.sources.map((s) => ({ value: s, label: s })),
            value: sources,
            onChange: (v) => setSources(v as string[]),
            multiple: true,
          },
          {
            key: "funnel",
            label: "All Funnels",
            options: filterOptions.funnels.map((f) => ({ value: f.value, label: f.label })),
            value: funnels,
            onChange: (v) => setFunnels(v as string[]),
            multiple: true,
          },
          {
            key: "leadType",
            label: "All Lead Types",
            options: [
              { value: "applicant", label: "Applicant" },
              { value: "optin", label: "Opt-in" },
            ],
            value: leadTypes,
            onChange: (v) => setLeadTypes(v as string[]),
            multiple: true,
          },
          {
            key: "content",
            label: "Select Content",
            options: filterOptions.contents.map((c) => ({ value: c.value, label: c.label.length > 30 ? c.label.slice(0, 30) + "..." : c.label })),
            value: contents,
            onChange: (v) => setContents(v as string[]),
            multiple: true,
          },
          {
            key: "closer",
            label: "Select Closers",
            options: filterOptions.closers.map((c) => ({ value: c, label: c })),
            value: closers,
            onChange: (v) => setClosers(v as string[]),
            multiple: true,
          },
          {
            key: "setter",
            label: "Select Setters",
            options: filterOptions.setters.map((s) => ({ value: s, label: s })),
            value: setters,
            onChange: (v) => setSetters(v as string[]),
            multiple: true,
          },
        ]}
        onClearAll={handleClearAll}
      />

      {/* Main Metric Cards */}
      <div className="grid grid-cols-4 gap-3">
        <MetricCard
          label="Cash Collected"
          value={formatCurrencyAbbreviated(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="green"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={0}
          compact
          onClick={() => setIsCashByCreatorOpen(true)}
        />
        <MetricCard
          label="Revenue"
          value={formatCurrencyAbbreviated(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="brand"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={1}
          compact
        />
        <MetricCard
          label="Net Cash After Refunds"
          value={formatCurrencyAbbreviated(metrics?.netCashAfterRefunds || 0)}
          icon={<DollarIcon />}
          color="green"
          data={[]}
          tooltipFormatter={formatCurrency}
          index={2}
          compact
        />
        <MetricCard
          label="Close Rate"
          value={formatPercent(metrics?.closeRate || 0)}
          icon={<TargetIcon />}
          color="blue"
          data={[]}
          tooltipFormatter={(v) => formatPercent(v)}
          index={3}
          compact
        />
      </div>

      {/* Call Statistics */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-6">
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={0} />
        <StatCard label="Calls Due (Scheduler)" value={(metrics?.schedulerCallsDue || 0).toLocaleString()} index={1} />
        <StatCard label="Calls Cancelled" value={(metrics?.cancelledCalls || 0).toLocaleString()} index={2} />
        <StatCard label="Calls Due (EOCs)" value={(metrics?.callsDue || 0).toLocaleString()} index={3} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={4} />
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={5} />
      </div>

      {/* Average Metrics */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <StatCard label="Avg. Cash/Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={4} />
        <StatCard label="Avg. Cash/Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={5} />
        <StatCard label="Avg. Cash/Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={6} />
        <StatCard label="Avg. Cash/Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={7} />
      </div>

      {/* Stage cash totals: applications, opt-ins, and refunds. */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <AdditionalStatCard
          label="Total Cash Applications"
          value={formatCurrency(metrics?.totalApplicationsCash || 0)}
          description={`Full applications: ${formatCurrency(metrics?.totalFullApplicationsCash || 0)} · Partial applications: ${formatCurrency(metrics?.totalPartialApplicationsCash || 0)}`}
          index={8}
        />
        <AdditionalStatCard
          label="Total Cash Opt-ins"
          value={formatCurrency(metrics?.totalOptinsCash || 0)}
          description="Opted in, did not apply or book, then closed through outbound"
          index={9}
        />
        <div
          className="cursor-pointer"
          onClick={() => openDrilldown("refunds")}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") openDrilldown("refunds"); }}
        >
          <AdditionalStatCard
            label="Refunds"
            value={formatCurrency(metrics?.refunds || 0)}
            description="Click to view refunded leads and source attribution"
            index={10}
          />
        </div>
      </div>

      {/* Outbound and self-booked inbound cash are complementary conversion-path rollups. */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <AdditionalStatCard
          label="Total Cash Outbound"
          value={formatCurrency(metrics?.totalOutboundCash || 0)}
          description="Applications that did not self-book, partial applications, and opt-ins"
          index={11}
        />
        <AdditionalStatCard
          label="Total Cash Inbound Booked Calls"
          value={formatCurrency(metrics?.totalInboundCash || 0)}
          description="Full applications followed by an Application Self-Booking"
          index={12}
        />
      </div>

      {/* Cash & DM Row */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        {[
          { label: "New Cash Collected", value: formatCurrency(metrics?.newCashCollected || 0), description: "Total cash from new deals (excludes MRR)" },
          { label: "MRR", value: formatCurrency(metrics?.mrrCash || 0), description: "Monthly recurring revenue" },
          { label: "Total Gross Revenue", value: formatCurrency(metrics?.totalGrossRevenue || 0), description: "Total contract value (all revenue)" },
          { label: "Total DM Bookings", value: (metrics?.totalDMBookings || 0).toLocaleString() },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.5 + i * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
            whileHover={{ y: -3, boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)", transition: { duration: 0.2 } }}
            className="bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-border/80"
          >
            <motion.p className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 + i * 0.06 }}>
              {stat.label}
            </motion.p>
            <p className="text-xl md:text-3xl font-bold">{stat.value}</p>
            {stat.description && (
              <motion.p className="text-xs text-muted-foreground mt-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + i * 0.06 }}>
                {stat.description}
              </motion.p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Lead Counts & DQ Row */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        {/* Total Applications */}
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          whileHover={{ y: -3, boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)", transition: { duration: 0.2 } }}
          className="bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-border/80"
        >
          <p className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2">Total Applications</p>
          <p className="text-xl md:text-3xl font-bold">{(metrics?.totalApplications || 0).toLocaleString()}</p>
        </motion.div>

        {/* Total Opt-ins */}
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.56, ease: [0.25, 0.46, 0.45, 0.94] }}
          whileHover={{ y: -3, boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)", transition: { duration: 0.2 } }}
          className="bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-border/80"
        >
          <p className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2">Total Opt-ins</p>
          <p className="text-xl md:text-3xl font-bold">{(metrics?.totalOptins || 0).toLocaleString()}</p>
        </motion.div>

        {/* Cancel Rate — clickable */}
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.62, ease: [0.25, 0.46, 0.45, 0.94] }}
          whileHover={{ y: -3, boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)", transition: { duration: 0.2 } }}
          onClick={() => openDrilldown("cancelledCalls")}
          className="bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-[var(--brand-primary)] cursor-pointer"
        >
          <p className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2 flex items-center gap-1">
            Cancel Rate
            <svg className="w-3.5 h-3.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          </p>
          <p className="text-xl md:text-3xl font-bold">{formatPercent(metrics?.cancelRate || 0)}</p>
          <p className="text-xs text-muted-foreground mt-1">{(metrics?.callsBookedForCancelRate || 0).toLocaleString()} scheduled appointments · {(metrics?.cancelledCalls || 0).toLocaleString()} cancelled (booking status)</p>
        </motion.div>

        {/* Financial DQs — clickable */}
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.68, ease: [0.25, 0.46, 0.45, 0.94] }}
          whileHover={{ y: -3, boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)", transition: { duration: 0.2 } }}
          onClick={() => openDrilldown("financialDQ")}
          className="bg-card rounded-xl border-2 border-border p-4 md:p-6 transition-colors duration-300 hover:border-[var(--brand-primary)] cursor-pointer"
        >
          <p className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 md:mb-2 flex items-center gap-1">
            Financial DQs
            <svg className="w-3.5 h-3.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          </p>
          <p className="text-xl md:text-3xl font-bold">{(metrics?.financialDQCount || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">Financial DQ + Cancelled by sales</p>
        </motion.div>
      </div>

      {/* Closes by Country Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Country
            </h3>
            <AnimatePresence>
              {country && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setCountry("")}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <span>Clear: {country}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[400px] cursor-pointer">
          <ResponsivePie
            data={toNivoPieData(countryData, chartColors)}
            margin={{ top: 40, right: 120, bottom: 40, left: 120 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => String(d.label)}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            onClick={(node) => handleCountryClick(String(node.label))}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Cash Collected Trend and Lead Source */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Cash Collected Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <h3 className="text-lg font-semibold mb-4">
            Cash Collected Trend
            {trendType === "daily" && (
              <span className="text-xs font-normal text-muted-foreground ml-2">(Daily)</span>
            )}
          </h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashTrend} margin={{ top: 10, right: 5, left: -10, bottom: 10 }}>
                <defs>
                  <pattern
                    id="gridPattern"
                    x="0"
                    y="0"
                    width="20"
                    height="40"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 20 0 L 0 0 0 20"
                      fill="none"
                      stroke="hsl(var(--border))"
                      strokeWidth="0.5"
                      strokeOpacity="0.5"
                    />
                  </pattern>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={areaStroke} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={areaStroke} stopOpacity={0.1} />
                  </linearGradient>
                  <filter id="dotShadow" x="-100%" y="-100%" width="300%" height="300%">
                    <feDropShadow dx="2" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.4)" />
                  </filter>
                </defs>
                <rect
                  x="32"
                  y="0"
                  width="100%"
                  height="100%"
                  fill="url(#gridPattern)"
                  style={{ pointerEvents: "none" }}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: "#a1a1aa" }}
                  tickMargin={6}
                  interval="preserveStartEnd"
                  tickFormatter={(val) => {
                    if (trendType === "monthly") {
                      const [year, month] = val.split("-");
                      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                      const monthName = monthNames[parseInt(month, 10) - 1] || month;
                      return `${monthName} ${year}`;
                    }
                    const [, month, day] = val.split("-");
                    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                    const monthName = monthNames[parseInt(month, 10) - 1] || month;
                    return `${monthName} ${parseInt(day, 10)}`;
                  }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 9, fill: "#a1a1aa" }}
                  tickFormatter={(val) => {
                    if (val === 0) return "$0";
                    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
                    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
                    return `$${val}`;
                  }}
                  tickMargin={2}
                  width={32}
                />
                <Tooltip
                  cursor={{ stroke: areaStroke, strokeWidth: 1 }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      let displayLabel = label;
                      if (trendType === "monthly" && typeof label === "string") {
                        const [year, month] = label.split("-");
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const monthName = monthNames[parseInt(month || "", 10) - 1] || month;
                        displayLabel = `${monthName} ${year}`;
                      } else if (typeof label === "string") {
                        const [year, month, day] = label.split("-");
                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const monthName = monthNames[parseInt(month || "", 10) - 1] || month;
                        displayLabel = `${monthName} ${parseInt(day || "", 10)}, ${year}`;
                      }
                      return (
                        <div className="rounded-lg bg-card border border-border p-3 shadow-lg">
                          <div className="text-xs font-medium mb-1 text-muted-foreground">{displayLabel}</div>
                          <div className="text-xs font-medium mb-1 text-muted-foreground">Cash:</div>
                          <div className="text-sm font-semibold text-foreground">{formatCurrency(payload[0]?.value as number)}</div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={areaStroke}
                  strokeWidth={2}
                  fill="url(#areaGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: areaStroke,
                    stroke: "white",
                    strokeWidth: 2,
                    filter: "url(#dotShadow)",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Closes by Lead Source */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">
                {chartFilterMode === "closed" ? "Closes" : "Leads"} By Lead Source
              </h3>
              <AnimatePresence>
                {source && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => setSource("")}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <span>Clear: {source}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
            <motion.select
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              value={chartFilterMode}
              onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
              className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
            >
              <option value="closed">Closed Leads</option>
              <option value="all">All Leads</option>
            </motion.select>
          </div>
          <div className="h-[300px] cursor-pointer">
            <ResponsivePie
              data={toNivoPieData(leadSourceData, chartColors)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              arcLinkLabelsStraightLength={15}
              arcLinkLabelsDiagonalLength={15}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              onClick={(node) => handleSourceClick(String(node.label))}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>
      </div>

      {/* Closes by Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Content
            </h3>
            <AnimatePresence>
              {contents.length > 0 && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  onClick={() => setContents([])}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                >
                  <span>Clear: {contents.length > 1 ? `${contents.length} selected` : (contents[0].length > 20 ? contents[0].slice(0, 20) + "..." : contents[0])}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </div>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[500px] cursor-pointer">
          <ResponsivePie
            data={toNivoPieData(contentData, chartColors)}
            margin={{ top: 40, right: 140, bottom: 40, left: 140 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={8}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => {
              const label = String(d.label);
              return label.length > 25 ? label.slice(0, 22) + "..." : label;
            }}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            onClick={(node) => handleContentClick(String(node.label))}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Recent Calls Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Recent Calls</h3>
          <p className="text-sm text-muted-foreground">Latest call activity</p>
        </div>
        <DataTable
          data={recentCalls}
          columns={recentCallsColumns}
          rowKey="id"
          emptyMessage="No recent calls found."
          loading={tableLoading}
          serverPagination={{
            currentPage: tablePage,
            pageSize: tablePageSize,
            totalItems: tableTotalItems,
            totalPages: tableTotalPages,
            pageSizeOptions: [10, 25, 50, 100],
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          onEdit={handleEditCall}
          onDelete={handleDeleteCall}
          onRowClick={(call) => call.email && setSelectedLeadEmail(call.email)}
        />
      </motion.div>

      {/* Edit Dialog */}
      <EditRowDialog
        open={!!editingCall}
        onOpenChange={(open) => !open && setEditingCall(null)}
        title="Edit Call"
        description="Update call information"
        fields={CALL_EDIT_FIELDS}
        data={editingCall as Record<string, unknown> | null}
        onSave={handleSaveCall}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={!!deletingCall}
        onOpenChange={(open) => !open && setDeletingCall(null)}
        title="Delete Call"
        itemName={deletingCall?.leadName}
        onConfirm={confirmDeleteCall}
      />

      {/* Lead Timeline Drawer */}
      <AnimatePresence>
        {selectedLeadEmail && (
          <LeadTimelineDrawer
            email={selectedLeadEmail}
            onClose={() => setSelectedLeadEmail(null)}
          />
        )}
      </AnimatePresence>

      {/* Cash Collected by Creator modal */}
      <AnimatePresence>
        {isCashByCreatorOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setIsCashByCreatorOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="flex max-h-[85vh] w-full max-w-xl flex-col rounded-2xl border border-border bg-card shadow-2xl"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cash-by-creator-title"
            >
              <div className="flex items-start justify-between gap-4 border-b border-border p-5">
                <div>
                  <h2 id="cash-by-creator-title" className="text-lg font-semibold">Cash Collected by Creator</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Distribution for the current dashboard date range and filters.</p>
                </div>
                <button type="button" onClick={() => setIsCashByCreatorOpen(false)} className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Close cash by creator">✕</button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                {cashByCreator.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">No cash collected for this period.</div>
                ) : (
                  <div className="space-y-3">
                    {cashByCreator.map((creator) => (
                      <div key={creator.name} className="rounded-xl border border-border bg-muted/30 p-4">
                        <div className="flex items-baseline justify-between gap-4">
                          <span className="font-medium text-foreground">{creator.name}</span>
                          <span className="shrink-0 text-base font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{formatCurrency(creator.cash)}</span>
                        </div>
                        <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, Math.max(0, creator.percentage))}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">{formatPercent(creator.percentage)} of cash collected</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between border-t border-border px-5 py-4">
                <span className="text-sm text-muted-foreground">Total Cash Collected</span>
                <span className="text-lg font-semibold tabular-nums">{formatCurrency(metrics?.totalCashCollected || 0)}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Financial DQ / Cancelled Calls Drilldown Modal */}
      <AnimatePresence>
        {drilldownModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setDrilldownModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-border">
                <h2 className="text-lg font-semibold">
                  {drilldownModal.type === "financialDQ"
                    ? "Financial DQs"
                    : drilldownModal.type === "cancelledCalls"
                      ? "Cancelled Calls"
                      : "Refunded Leads"}
                </h2>
                <button onClick={() => setDrilldownModal(null)} className="text-muted-foreground hover:text-foreground transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* Filters */}
              <div className="px-5 py-3 border-b border-border grid grid-cols-2 gap-2">
                {(["sourceCreator", "sourcePlatform", "sourceFunnel", "sourceContent"] as const).map((key) => {
                  const optionsMap: Record<string, string[]> = {
                    sourceCreator: drilldownModal.filterOptions.creators,
                    sourcePlatform: drilldownModal.filterOptions.platforms,
                    sourceFunnel: drilldownModal.filterOptions.funnels,
                    sourceContent: drilldownModal.filterOptions.contents,
                  };
                  const labelMap: Record<string, string> = { sourceCreator: "Creator", sourcePlatform: "Platform", sourceFunnel: "Funnel", sourceContent: "Content" };
                  const options = optionsMap[key] || [];
                  return (
                    <select
                      key={key}
                      value={drilldownFilters[key]}
                      onChange={(e) => {
                        const newFilters = { ...drilldownFilters, [key]: e.target.value };
                        setDrilldownFilters(newFilters);
                        openDrilldown(drilldownModal.type, newFilters);
                      }}
                      className="text-xs rounded-lg border border-border bg-background px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
                    >
                      <option value="">All {labelMap[key]}s</option>
                      {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  );
                })}
              </div>

              {/* Leads list */}
              <div className="overflow-y-auto flex-1 p-4">
                {drilldownLoading ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">Loading...</div>
                ) : drilldownModal.leads.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">No leads found for this period.</div>
                ) : drilldownModal.type === "cancelledCalls" ? (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[860px] text-left text-sm">
                      <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 font-medium">Lead</th>
                          <th className="px-3 py-2 font-medium">Cancelled at (ET)</th>
                          <th className="px-3 py-2 font-medium">Scheduled call</th>
                          <th className="px-3 py-2 font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {drilldownModal.leads.map((lead, i) => (
                          <tr key={`${lead.email}-${i}`} className="align-top">
                            <td className="px-3 py-3">
                              <p className="font-medium">{lead.name}</p>
                              <p className="text-xs text-muted-foreground">{lead.email || "No email recorded"}</p>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{formatEasternDateTime(lead.cancelledAt)}</td>
                            <td className="px-3 py-3 whitespace-nowrap text-muted-foreground">{lead.scheduledCallAt ? formatEasternDateTime(lead.scheduledCallAt) : lead.date || "Not available"}</td>
                            <td className="px-3 py-3">
                              <p className="whitespace-pre-wrap leading-5">{lead.cancellationReason || "Not recorded"}</p>
                              {lead.reasonSource && <p className="mt-1 text-xs text-muted-foreground">{lead.reasonSource === "setter_thread" ? "Setter reply in Slack thread" : "Slack cancellation notice"}</p>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {drilldownModal.leads.map((lead, i) => (
                      <div key={i} className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{lead.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{lead.email}</p>
                            {lead.product && <p className="text-xs text-muted-foreground mt-0.5 truncate">{lead.product}</p>}
                            {lead.attribution && <p className="text-xs text-muted-foreground mt-0.5 capitalize">{lead.attribution} attribution</p>}
                            {(lead.callOutcome || lead.status) && <p className="text-xs text-muted-foreground mt-0.5">{lead.callOutcome || lead.status}</p>}
                          </div>
                          <div className="text-right shrink-0">
                            {lead.amount != null && <p className="text-sm font-semibold text-red-500">-{formatCurrency(lead.amount)}</p>}
                            {lead.date && <p className="text-xs text-muted-foreground">{lead.date}</p>}
                            {lead.closer && <p className="text-xs text-muted-foreground">{lead.closer}</p>}
                          </div>
                        </div>
                        {(lead.sourcePlatform || lead.sourceFunnel || lead.sourceCreator || lead.sourceContent) && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {lead.sourcePlatform && <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">📱 {lead.sourcePlatform}</span>}
                            {lead.sourceFunnel && <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">🔀 {lead.sourceFunnel}</span>}
                            {lead.sourceCreator && <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">👤 {lead.sourceCreator}</span>}
                            {lead.sourceContent && <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">🎬 {lead.sourceContent}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-border flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{drilldownModal.leads.length} lead{drilldownModal.leads.length !== 1 ? "s" : ""}</span>
                {Object.values(drilldownFilters).some(Boolean) && (
                  <button
                    onClick={() => {
                      const cleared = { sourceCreator: "", sourcePlatform: "", sourceFunnel: "", sourceContent: "" };
                      setDrilldownFilters(cleared);
                      openDrilldown(drilldownModal.type, cleared);
                    }}
                    className="text-xs text-[var(--brand-primary)] hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
